
import React, { useState } from 'react';
import { UserTechnique, Technique } from '@/types/technique';
import { EditTechniqueDialog } from './EditTechniqueDialog';
import { TechniquesPerPageSelector } from '../technique/TechniquesPerPageSelector';
import { TechniquesGrid } from './grid/TechniquesGrid';
import { TechniquesPagination } from './pagination/TechniquesPagination';
import { TechniquesLoadingState } from './loading/TechniquesLoadingState';
import { useTechniquesPagination } from '@/hooks/useTechniquesPagination';
import { useTechniqueVideos } from '@/hooks/useTechniqueVideos';

interface MyTechniquesListProps {
  filteredTechniques: UserTechnique[];
  onRemoveTechnique: (id: string, e: React.MouseEvent) => void;
  isLoading: boolean;
  itemsPerPage?: number;
  onItemsPerPageChange?: (value: number) => void;
}

export const MyTechniquesList: React.FC<MyTechniquesListProps> = ({
  filteredTechniques,
  onRemoveTechnique,
  isLoading,
  itemsPerPage = 9,
  onItemsPerPageChange
}) => {
  const [editingTechnique, setEditingTechnique] = useState<{ technique: Technique, notes: string } | null>(null);
  const { techniquesWithVideos } = useTechniqueVideos(filteredTechniques);
  
  const {
    currentPage,
    totalPages,
    paginatedItems: paginatedTechniques,
    goToNextPage,
    goToPrevPage
  } = useTechniquesPagination(filteredTechniques, itemsPerPage);

  // Handle edit click for user-created techniques
  const handleEditClick = (e: React.MouseEvent, technique: Technique, notes: string) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingTechnique({ technique, notes });
  };

  if (isLoading) {
    return <TechniquesLoadingState />;
  }

  if (filteredTechniques.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <TechniquesGrid
        techniques={paginatedTechniques}
        techniquesWithVideos={techniquesWithVideos || {}}
        onRemoveTechnique={onRemoveTechnique}
        onEditTechnique={handleEditClick}
      />
      
      <div className="flex flex-col items-center gap-3">
        <TechniquesPagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onNextPage={goToNextPage}
          onPrevPage={goToPrevPage}
        />
        
        {onItemsPerPageChange && (
          <TechniquesPerPageSelector 
            itemsPerPage={itemsPerPage} 
            onItemsPerPageChange={onItemsPerPageChange} 
          />
        )}
      </div>
      
      {/* Edit technique dialog */}
      {editingTechnique && (
        <EditTechniqueDialog
          open={!!editingTechnique}
          onOpenChange={(open) => {
            if (!open) setEditingTechnique(null);
          }}
          technique={editingTechnique.technique}
          notes={editingTechnique.notes}
          onSuccess={() => {
            // Force refetch by triggering a state change
            window.location.reload();
          }}
        />
      )}
    </div>
  );
};
